import React, { FC, useState } from "react";
import { useScreenSize } from "../../../../hooks/useScreenSize";
import { Location, LocationProps } from "../Location";
import { NavMenuItem } from "../NavMenuItem";
import { UserMobile } from "../UserMobile";
import { UserProps } from "../User";
import { StyledLink, StyledMenu } from "./index.styled";

interface NavItem {
  title: string;
  link?: string;
  links?: {
    display: string;
    url: string;
    id?: string;
  }[];
  isOpenNewTab?: boolean;
  id?: string;
}

export interface NavLinksProps {
  navItems: NavItem[];
  onClickHandler?: React.MouseEventHandler;
}

interface NavMenuProps {
  hasSubNavs?: boolean;
  isOpen?: boolean;
  keepMobileMenuOpen?: (isOpen: boolean) => void;
  location?: LocationProps;
  navLinks: NavLinksProps;
  user: UserProps;
}

export const NavMenu: FC<NavMenuProps> = ({
  hasSubNavs,
  isOpen = false,
  keepMobileMenuOpen,
  location,
  navLinks,
  user,
}) => {
  const { navItems, onClickHandler } = navLinks;

  const customHTMLAttributeName = "data-identifier";
  const [keyOfOpenNavItem, setIndex] = useState("");
  const { isMobile } = useScreenSize();

  const closeAllMenus = (): void => {
    setIndex("");
  };

  const openSpecificMenu = (event: React.MouseEvent): void => {
    const key = event.currentTarget.getAttribute(customHTMLAttributeName) || "";
    setIndex(key);
  };

  const toggleMenu = (event: React.MouseEvent | React.TouchEvent): void => {
    const key = event.currentTarget.getAttribute(customHTMLAttributeName) || "";
    // if the key matches keyOfOpenNavItem, it's already open, so toggle it closed
    setIndex(key === keyOfOpenNavItem ? "" : key);
  };

  const onClickWrapper = (event: React.MouseEvent): void => {
    // close the menus
    closeAllMenus();
    // close the mobile menu
    if (keepMobileMenuOpen) {
      keepMobileMenuOpen(false);
    }
    // continue with the original click handler
    onClickHandler && onClickHandler(event);
  };

  const onKeyDownHandler = (event: React.KeyboardEvent): void => {
    // To enable keyboard access on navigation menus
    // - enter / space key opens menu (enter again at the top closes menu)
    // - escape closes menu
    switch (event.key) {
      case "Enter":
      case " ": {
        const key =
          event.currentTarget.getAttribute(customHTMLAttributeName) || "";
        setIndex(key === keyOfOpenNavItem ? "" : key);
        return;
      }
      case "Escape": {
        closeAllMenus();
        return;
      }
      default:
        return;
    }
  };

  return (
    <StyledMenu isMobileMenuOpen={isOpen} hasSubNavs={hasSubNavs}>
      <UserMobile user={user} />

      {isMobile && location && (
        <Location location={location} keepMobileMenuOpen={keepMobileMenuOpen} />
      )}

      {navItems.map((navItem, index) => {
        if (navItem.links) {
          return (
            <NavMenuItem
              id={navItem.id}
              identifier={`${navItem.title.replace(/ /g, "")}${index}`}
              isMobile={isOpen}
              isOpen={
                `${navItem.title.replace(/ /g, "")}${index}` ===
                keyOfOpenNavItem
              }
              onClickHandler={onClickWrapper}
              onKeyDown={onKeyDownHandler}
              onMouseEnter={openSpecificMenu}
              onMouseLeave={closeAllMenus}
              onTouchStart={toggleMenu}
              key={index}
              links={navItem.links || []}
              title={navItem.title}
            />
          );
        } else {
          return (
            <StyledLink
              data-testid={`ui-header-nav-menu-link-${navItem.title.replace(
                / /g,
                "-"
              )}`}
              href={navItem.link || ""}
              id={navItem.id}
              key={index}
              onClick={onClickWrapper}
              target={navItem.isOpenNewTab ? `_blank` : `_self`}
            >
              {navItem.title}
            </StyledLink>
          );
        }
      })}
    </StyledMenu>
  );
};
